import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { formatScore } from "../utils/format";
import { PowerIcon } from "../components/SkillIcons";
import SearchEventCard from "../components/SearchEventCard";
import { useIsMobile } from "../hooks/useIsMobile";

type Kind = "member" | "venue" | "ritual" | "community";
type RecentSearch = { kind: Kind; id: string; label: string; subtitle?: string; avatar_url?: string };

const RECENT_KEY = "oltodo_recent_searches";
const loadRecent = (): RecentSearch[] => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
};
const saveRecent = (list: RecentSearch[]) => localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5)));

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

const BackIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);
const SearchIconSmall = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/>
  </svg>
);

const KIND_LABEL: Record<Kind, string> = { member: "Profils", venue: "Lieux", ritual: "Rituels", community: "Communautés" };

// Rituels pliés dans "Sorties" (un rituel est fondamentalement une sortie récurrente, même carte
// visuelle qu'une Passion/Power), correspondances Power/Passion pliées dans "Membres" (ce sont
// des profils, juste trouvés via une autre voie) — 4 onglets plutôt que les 3 de l'app de
// référence, pour ne perdre ni les lieux ni les rituels d'OLTODO qui n'ont pas de case naturelle
// dans un découpage strict Membres/Communautés/Sorties.
type ResultTab = "members" | "communities" | "events" | "venues";

export default function Search() {
  const navigate = useNavigate();
  const { user, lang } = useApp();
  const isMobile = useIsMobile();

  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<{ scope?: "nearby" | "global"; members: any[]; venues: any[]; rituals: any[]; communities: any[]; people_by_skill: any[]; events: any[] } | null>(null);
  const [recent, setRecent] = useState<RecentSearch[]>(loadRecent());
  const [suggestedMembers, setSuggestedMembers] = useState<any[]>([]);
  const [suggestedVenues, setSuggestedVenues] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // "À proximité" par défaut dès qu'une position est connue (comme Explorer) — jamais "France
  // entière" en dur, OLTODO n'est pas un produit franco-français. Bascule masquée si aucune
  // position n'est connue : "à proximité" n'aurait alors aucun sens à proposer.
  const hasLocation = !!(user?.latitude && user?.longitude);
  const [scope, setScope] = useState<"nearby" | "global">("nearby");
  const [activeTab, setActiveTab] = useState<ResultTab>("members");

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const params = user?.latitude && user?.longitude ? `?lat=${user.latitude}&lng=${user.longitude}` : "";
    api.get(`/users/discover${params}`).then(data => setSuggestedMembers((data?.users || []).slice(0, 8))).catch(() => {});
    api.get(`/venues/nearby${params}`).then(data => setSuggestedVenues((data?.venues || []).slice(0, 8))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recherche en direct pendant la frappe, façon Facebook/Instagram — un léger délai pour ne
  // pas mitrailler le serveur à chaque lettre tapée.
  useEffect(() => {
    if (!query.trim()) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const params = new URLSearchParams({ q: query.trim() });
    if (hasLocation) {
      params.set("lat", String(user!.latitude));
      params.set("lng", String(user!.longitude));
      params.set("scope", scope);
    }
    const timer = setTimeout(() => {
      api.get(`/search?${params.toString()}`).then(data => setResults(data)).catch(() => {}).finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, scope, hasLocation]);

  const pushRecent = (item: RecentSearch) => {
    const list = loadRecent().filter(r => !(r.kind === item.kind && r.id === item.id));
    list.unshift(item);
    saveRecent(list);
    setRecent(loadRecent());
  };
  const removeRecent = (kind: Kind, id: string) => {
    saveRecent(loadRecent().filter(r => !(r.kind === kind && r.id === id)));
    setRecent(loadRecent());
  };

  const goToMember = (m: any) => { pushRecent({ kind: "member", id: m.id, label: m.name, subtitle: m.city, avatar_url: m.avatar_url }); navigate(`/profile/${m.id}`); };
  const goToVenue = (v: any) => { pushRecent({ kind: "venue", id: v.id, label: v.name, subtitle: v.type, avatar_url: v.cover_url }); navigate(`/venue/${v.id}`); };
  const goToCommunity = (c: any) => { pushRecent({ kind: "community", id: c.id, label: c.name, subtitle: `${c.members_count || 0} membres`, avatar_url: c.cover_url }); navigate(`/community/${c.id}`); };
  // Les rituels n'ont pas de page dédiée dans l'appli (on les rejoint directement) — cliquer un
  // rituel récent relance simplement sa recherche, prêt à afficher le bouton "Rejoindre".
  const goToRitual = (r: any) => { pushRecent({ kind: "ritual", id: r.id, label: r.title, subtitle: r.frequency }); navigate(`/ritual/${r.id}`); };
  const joinRitual = (id: string) => { api.post(`/rituals/${id}/join`, {}).catch(() => {}); };
  const goToEvent = (e: any) => { navigate(`/publication/${e.id}`); };

  const goToRecent = (r: RecentSearch) => {
    if (r.kind === "member") navigate(`/profile/${r.id}`);
    else if (r.kind === "venue") navigate(`/venue/${r.id}`);
    else if (r.kind === "community") navigate(`/community/${r.id}`);
    else setQuery(r.label);
  };

  const row = (opts: { key: string; avatarUrl?: string; rounded?: boolean; title: string; subtitle?: React.ReactNode; onClick: () => void; action?: { label: string; onClick: () => void } }) => (
    <div key={opts.key} onClick={opts.onClick} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 4px", cursor: "pointer" }}>
      <div style={{ width: 46, height: 46, borderRadius: opts.rounded === false ? 14 : "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
        {opts.avatarUrl ? <img src={opts.avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(opts.title)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{opts.title}</div>
        {opts.subtitle && <div style={{ fontSize: 12, color: "#999", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{opts.subtitle}</div>}
      </div>
      {opts.action && (
        <button onClick={e => { e.stopPropagation(); opts.action!.onClick(); }} style={{ padding: "7px 14px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
          {opts.action.label}
        </button>
      )}
    </div>
  );

  const suggestedCard = (item: any, onClick: () => void, img?: string) => (
    <div key={item.id} onClick={onClick} style={{ position: "relative", width: 140, flexShrink: 0, borderRadius: 18, overflow: "hidden", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}>
      <div style={{
        height: 172, display: "flex", alignItems: "flex-end", padding: "0 10px 10px",
        backgroundColor: "#0a0a0a",
        backgroundImage: img ? `linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.75) 100%), url(${img})` : "linear-gradient(160deg, #0a0a0aCC 0%, #0a0a0a 100%)",
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        <div style={{ color: "white", fontWeight: 700, fontSize: 13.5, lineHeight: 1.25 }}>{item.name}</div>
      </div>
      {item.is_online && <div style={{ position: "absolute", top: 8, right: 8, width: 11, height: 11, borderRadius: "50%", backgroundColor: "#4CAF50", border: "2px solid white" }} />}
    </div>
  );

  const hasResults = results && (results.members.length || results.venues.length || results.rituals.length || results.communities.length || results.people_by_skill.length || results.events.length);
  const noResults = results && !hasResults && !searching;

  // Compteurs par onglet — purement côté client à partir de la même réponse déjà renvoyée par
  // /search, aucun second aller-retour nécessaire.
  const tabCounts: Record<ResultTab, number> = {
    members: (results?.members.length || 0) + (results?.people_by_skill.length || 0),
    communities: results?.communities.length || 0,
    events: (results?.events.length || 0) + (results?.rituals.length || 0),
    venues: results?.venues.length || 0,
  };
  const TABS: { key: ResultTab; label: string }[] = [
    { key: "members", label: t(lang, "search_tab_members") || "Membres" },
    { key: "communities", label: t(lang, "search_tab_communities") || "Communautés" },
    { key: "events", label: t(lang, "search_tab_events") || "Sorties" },
    { key: "venues", label: t(lang, "search_tab_venues") || "Lieux" },
  ];
  // Si l'onglet actif se retrouve vide après une nouvelle frappe (ex: "Lieux" n'a plus aucun
  // résultat), retomber sur le premier onglet qui en a un plutôt que de laisser un onglet
  // sélectionné mais visuellement mort.
  useEffect(() => {
    if (!results) return;
    if (tabCounts[activeTab] > 0) return;
    const firstWithResults = TABS.find(tb => tabCounts[tb.key] > 0);
    if (firstWithResults) setActiveTab(firstWithResults.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  return (
    <div style={isMobile ? { height: "100%", display: "flex", flexDirection: "column", backgroundColor: "white" } : { maxWidth: 680, margin: "0 auto", padding: "20px 16px 40px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "0 0 20px", flexShrink: 0 }}>
        <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", padding: 4, display: "flex", flexShrink: 0 }}>
          <BackIcon />
        </button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, backgroundColor: "#F5F5F5", borderRadius: 20, padding: "10px 14px" }}>
          <SearchIconSmall />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Rechercher un profil, une passion, un power, un lieu..."
            style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 14, minWidth: 0 }}
          />
          {query && <button onClick={() => setQuery("")} style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 15, flexShrink: 0 }}>✕</button>}
        </div>
      </div>

      <div style={isMobile ? { flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "0 16px 24px" } : {}}>

        {/* Résultats de recherche en direct */}
        {query.trim() && (
          <div>
            {searching && !results && <div style={{ textAlign: "center", padding: "24px 0", color: "#999", fontSize: 13 }}>Recherche…</div>}
            {noResults && <div style={{ textAlign: "center", padding: "24px 0", color: "#999", fontSize: 13 }}>Aucun résultat pour « {query.trim()} »</div>}

            {hasResults ? (
              <>
                {/* Bascule À proximité / Partout — masquée si aucune position connue. */}
                {hasLocation && (
                  <div style={{ display: "flex", gap: 8, padding: "12px 4px 4px" }}>
                    {(["nearby", "global"] as const).map(s => (
                      <button
                        key={s}
                        onClick={() => setScope(s)}
                        style={{
                          padding: "6px 14px", borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                          border: scope === s ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0",
                          backgroundColor: scope === s ? "#0a0a0a" : "white", color: scope === s ? "white" : "#666",
                        }}
                      >
                        {s === "nearby" ? (t(lang, "search_scope_nearby") || "À proximité") : (t(lang, "search_scope_global") || "Partout")}
                      </button>
                    ))}
                  </div>
                )}

                {/* Onglets à compteur, même motif que Notifications.tsx. */}
                <div className="no-scrollbar" style={{ display: "flex", gap: 8, overflowX: "auto", padding: "10px 4px", borderBottom: "1px solid #F0F0F0" }}>
                  {TABS.map(tb => {
                    const count = tabCounts[tb.key];
                    const active = activeTab === tb.key;
                    return (
                      <button
                        key={tb.key}
                        onClick={() => setActiveTab(tb.key)}
                        disabled={count === 0}
                        style={{
                          display: "flex", alignItems: "center", gap: 6, flexShrink: 0, padding: "8px 14px", borderRadius: 20,
                          border: "none", cursor: count === 0 ? "default" : "pointer",
                          backgroundColor: active ? "#0a0a0a" : "transparent",
                          color: active ? "white" : count === 0 ? "#ccc" : "#666",
                          fontSize: 13, fontWeight: 700,
                        }}
                      >
                        {tb.label}
                        {count > 0 && (
                          <span style={{
                            minWidth: 16, height: 16, borderRadius: 8, padding: "0 4px", fontSize: 10, fontWeight: 700,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            backgroundColor: active ? "rgba(255,255,255,0.25)" : "#0a0a0a", color: active ? "white" : "white",
                          }}>{count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div style={{ paddingTop: 8 }}>
                  {activeTab === "members" && (
                    <>
                      {results!.members.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5, padding: "12px 4px 2px" }}>Profils</div>
                          {results!.members.map(m => row({ key: "m" + m.id, avatarUrl: m.avatar_url, title: m.name, subtitle: `${m.city || "—"} · ★ ${formatScore(m.score)}`, onClick: () => goToMember(m) }))}
                        </div>
                      )}
                      {results!.people_by_skill.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5, padding: "12px 4px 2px" }}>Passions &amp; Powers</div>
                          {results!.people_by_skill.map(m => row({
                            key: "ps" + m.id, avatarUrl: m.avatar_url, title: m.name,
                            subtitle: (
                              <>
                                {m.matches.map((mt: any, i: number) => (
                                  <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                                    {i > 0 && " · "}
                                    {mt.type === "power" && <PowerIcon size={10} color="#999" />} {mt.label}
                                  </span>
                                ))}
                              </>
                            ),
                            onClick: () => goToMember(m),
                          }))}
                        </div>
                      )}
                    </>
                  )}

                  {activeTab === "events" && (
                    <>
                      {results!.events.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          {results!.events.map(e => <SearchEventCard key={"ev" + e.id} event={e} lang={lang} onClick={() => goToEvent(e)} />)}
                        </div>
                      )}
                      {results!.rituals.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: 0.5, padding: "12px 4px 2px" }}>Rituels</div>
                          {results!.rituals.map(r => row({ key: "r" + r.id, rounded: false, title: r.title, subtitle: `${r.frequency} · ${r.members_count || 0} participants`, onClick: () => goToRitual(r), action: { label: "Rejoindre", onClick: () => joinRitual(r.id) } }))}
                        </div>
                      )}
                    </>
                  )}

                  {activeTab === "venues" && results!.venues.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      {results!.venues.map(v => row({ key: "v" + v.id, avatarUrl: v.cover_url, rounded: false, title: v.name, subtitle: v.type || "Lieu", onClick: () => goToVenue(v) }))}
                    </div>
                  )}

                  {activeTab === "communities" && results!.communities.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      {results!.communities.map(c => row({ key: "c" + c.id, avatarUrl: c.cover_url, rounded: false, title: c.name, subtitle: `${c.members_count || 0} membres`, onClick: () => goToCommunity(c) }))}
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* État par défaut : historique + suggestions, comme Facebook */}
        {!query.trim() && (
          <div>
            {recent.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, padding: "14px 4px 4px" }}>Recherches récentes</div>
                {recent.map(r => (
                  <div key={r.kind + r.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 4px" }}>
                    <div onClick={() => goToRecent(r)} style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0, cursor: "pointer" }}>
                      <div style={{ width: 40, height: 40, borderRadius: r.kind === "member" ? "50%" : 12, overflow: "hidden", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", color: "#0a0a0a", flexShrink: 0 }}>
                        {r.avatar_url ? <img src={r.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ClockIcon />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.label}</div>
                        <div style={{ fontSize: 11.5, color: "#999" }}>{KIND_LABEL[r.kind]}{r.subtitle ? ` · ${r.subtitle}` : ""}</div>
                      </div>
                    </div>
                    <button onClick={() => removeRecent(r.kind, r.id)} style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 16, padding: 6, flexShrink: 0 }}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {suggestedMembers.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, padding: "4px 4px 10px" }}>Profils suggérés</div>
                <div className="no-scrollbar" style={{ display: "flex", gap: 12, overflowX: "auto", overflowY: "hidden", touchAction: "pan-x" }}>
                  {suggestedMembers.map(m => suggestedCard(m, () => goToMember(m), m.avatar_url))}
                </div>
              </div>
            )}

            {suggestedVenues.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, padding: "4px 4px 10px" }}>Lieux suggérés</div>
                <div className="no-scrollbar" style={{ display: "flex", gap: 12, overflowX: "auto", overflowY: "hidden", touchAction: "pan-x" }}>
                  {suggestedVenues.map(v => suggestedCard(v, () => goToVenue(v), v.cover_url))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`.no-scrollbar::-webkit-scrollbar{display:none} .no-scrollbar{scrollbar-width:none;-ms-overflow-style:none}`}</style>
    </div>
  );
}
